import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
} from '@mui/material';

import {
  startOfWeek,
  endOfWeek,
  format,
  addWeeks,
  eachDayOfInterval,
  startOfToday,
  parseISO,
  isSameDay,
} from '../utils/dateUtils';
import googleCalendarService from '../services/googleCalendar';
import ChatPanel from './ChatPanel';
import CalendarView from './CalendarView';
import { useChat } from '../contexts/ChatContext';

function EvaCalendar() {
  const navigate = useNavigate();
  const { 
    chatMessages, 
    setChatMessages, 
    message, 
    setMessage, 
    chatPanelWidth, 
    setChatPanelWidth,
    updateMessage 
  } = useChat();
  const [singleEvent, setSingleEvent] = useState();
  const [currentWeekStart, setCurrentWeekStart] = useState(() => 
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [isResizing, setIsResizing] = useState(false);

  // Calculate week dates dynamically
  const weekDates = useMemo(() => {
    const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
    return eachDayOfInterval({
      start: currentWeekStart,
      end: weekEnd,
    });
  }, [currentWeekStart]);

  // Format week range for header
  const weekRangeText = useMemo(() => {
    const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
    const startMonth = format(currentWeekStart, 'MMMM');
    const endMonth = format(weekEnd, 'MMMM');
    const startDay = format(currentWeekStart, 'd');
    const endDay = format(weekEnd, 'd');
    const year = format(currentWeekStart, 'yyyy');
    
    if (startMonth === endMonth) {
      return `${startMonth} ${startDay} - ${endDay}, ${year}`;
    } else {
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
    }
  }, [currentWeekStart]);

  // Format days for display
  const formattedDays = useMemo(() => {
    return weekDates.map(date => format(date, 'EEE dd'));
  }, [weekDates]);

  // Fetch events from Google Calendar
  const fetchEvents = useCallback(async () => {
    try {
      setIsLoading(true);
      const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
      const events = await googleCalendarService.getEvents(currentWeekStart, weekEnd);
      setGoogleEvents(events);
      setError(null);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to fetch calendar events. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentWeekStart]);

  // Initialize Google Calendar on component mount
  useEffect(() => {
    const initGoogleCalendar = async () => {
      try {
        setIsLoading(true);
        await googleCalendarService.initialize();
        
        // Check if user is already signed in by checking for access token
        const hasToken = !!googleCalendarService.getAccessToken();
        setIsSignedIn(hasToken);
        
        if (hasToken) {
          const profile = googleCalendarService.getProfile();
          setUserProfile(profile);
          await fetchEvents();
        }
      } catch (err) {
        console.error('Error initializing Google Calendar:', err);
        setError('Failed to initialize Google Calendar. Please check your API credentials.');
      } finally {
        setIsLoading(false);
      }
    };

    initGoogleCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch events when week changes
  useEffect(() => {
    if (isSignedIn) {
      fetchEvents();
    }
  }, [currentWeekStart, isSignedIn, fetchEvents]);

  useEffect(() => {
    // console.log('chatMessages', chatMessages);
  }, [chatMessages]);

  // Handle chat panel resize
  const handleMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const newWidth = e.clientX;
      const minWidth = 300;
      const calendarMinWidth = 400; // Minimum width for calendar to be fully visible
      const maxWidth = window.innerWidth - calendarMinWidth; // Ensure calendar always has minimum space
      
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setChatPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, setChatPanelWidth]);

  // Handle Google Calendar sign in
  const handleSignIn = async () => {
    try {
      setIsLoading(true);
      await googleCalendarService.signIn();
      setIsSignedIn(true);
      const profile = googleCalendarService.getProfile();
      setUserProfile(profile);
      await fetchEvents();
    } catch (err) {
      console.error('Error signing in:', err);
      setError('Failed to sign in to Google Calendar.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Google Calendar sign out
  const handleSignOut = async () => {
    try {
      await googleCalendarService.signOut();
      setIsSignedIn(false);
      setUserProfile(null);
      setGoogleEvents([]);
      setChatMessages([]); // Clear chat messages on sign out
      navigate('/');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  // Determine event type based on event properties
  const determineEventType = (googleEvent) => {
    const userEmail = localStorage.getItem('userEmail') || '';
    const userDomain = userEmail.split('@')[1] || '';
    const summary = (googleEvent.summary || '').toLowerCase();
    const description = (googleEvent.description || '').toLowerCase();
    const location = (googleEvent.location || '').toLowerCase();
    
    // Keywords for personal events
    const personalKeywords = [
      'personal', 'private', 'lunch', 'break', 'focus time', 
      'me time', 'family', 'vacation', 'holiday', 'time off'
    ];
    
    // Keywords for external events
    const externalKeywords = [
      'client', 'customer', 'vendor', 'partner', 'external',
      'sales', 'demo', 'meeting with', 'interview'
    ];
    
    // Check if it's a personal event
    const isPersonal = personalKeywords.some(keyword => 
      summary.includes(keyword) || 
      description.includes(keyword) ||
      location.includes(keyword)
    );
    
    if (isPersonal) {
      return 'personal';
    }
    
    // Check attendees for external emails
    if (googleEvent.attendees && Array.isArray(googleEvent.attendees)) {
      const hasExternalAttendee = googleEvent.attendees.some(attendee => {
        if (attendee.email) {
          const attendeeDomain = attendee.email.split('@')[1];
          // If attendee is not from same domain and not the user, it's external
          return attendeeDomain && attendeeDomain !== userDomain && !attendee.resource;
        }
        return false;
      });
      
      if (hasExternalAttendee) {
        return 'external';
      }
    }
    
    // Check organizer email domain
    if (googleEvent.organizer && googleEvent.organizer.email) {
      const organizerDomain = googleEvent.organizer.email.split('@')[1];
      if (organizerDomain && organizerDomain !== userDomain) {
        // Check if it's external domain
        const commonDomains = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com'];
        if (!commonDomains.includes(organizerDomain.toLowerCase())) {
          return 'external';
        }
      }
    }
    
    // Check for external keywords
    const isExternal = externalKeywords.some(keyword => 
      summary.includes(keyword) || 
      description.includes(keyword)
    );
    
    if (isExternal) {
      return 'external';
    }
    
    // Default to internal (company/organization meetings)
    return 'internal';
  };

  // Transform Google Calendar events to component format
  const calendarEvents = useMemo(() => {
    const events = {};
    const userEmail = localStorage.getItem('userEmail') || '';
    
    // Initialize all days with empty arrays
    weekDates.forEach((date) => {
      const dateKey = format(date, 'yyyy-MM-dd');
      events[dateKey] = [];
    });

    // Map Google Calendar events to our format
    googleEvents.forEach((googleEvent) => {
      let eventDate;
      let eventTime = '';
      
      // Handle both dateTime and date formats
      if (googleEvent.start.dateTime) {
        eventDate = parseISO(googleEvent.start.dateTime);
        eventTime = format(eventDate, 'h:mm a');
      } else if (googleEvent.start.date) {
        eventDate = parseISO(googleEvent.start.date);
        eventTime = 'All Day';
      } else {
        return; // Skip events without valid start time
      }

      // Find which day this event belongs to
      const matchingDate = weekDates.find(date => isSameDay(date, eventDate));
      if (matchingDate) {
        const dateKey = format(matchingDate, 'yyyy-MM-dd');
        
        // Determine event type: 'internal', 'external', or 'personal'
        const eventType = determineEventType(googleEvent);

        // Check if user has declined this event
        let isDeclined = false;
        if (googleEvent.attendees && Array.isArray(googleEvent.attendees)) {
          const userAttendee = googleEvent.attendees.find(
            attendee => attendee.email && attendee.email.toLowerCase() === userEmail.toLowerCase()
          );
          if (userAttendee && userAttendee.responseStatus === 'declined') {
            isDeclined = true;
          }
        }

        events[dateKey].push({
          id: googleEvent.id,
          time: eventTime,
          title: googleEvent.summary || 'No Title',
          eventType: eventType,
          description: googleEvent.description,
          location: googleEvent.location,
          isDeclined: isDeclined,
          status: googleEvent.status, // Add status field (cancelled, confirmed, tentative)
          attendees: googleEvent.attendees, // Add attendees array
          googleEvent: googleEvent, // Keep reference to original event
        });
      }
    });

    // Sort events by time within each day
    Object.keys(events).forEach(dateKey => {
      events[dateKey].sort((a, b) => {
        // All Day events come first
        if (a.time === 'All Day' && b.time !== 'All Day') return -1;
        if (b.time === 'All Day' && a.time !== 'All Day') return 1;
        if (a.time === 'All Day' && b.time === 'All Day') return 0;
        
        // Parse the actual datetime for proper chronological sorting
        const aDateTime = a.googleEvent.start?.dateTime;
        const bDateTime = b.googleEvent.start?.dateTime;
        
        if (!aDateTime || !bDateTime) return 0;
        
        const aTime = new Date(aDateTime).getTime();
        const bTime = new Date(bDateTime).getTime();
        
        return aTime - bTime;
      });
    });

    return events;
  }, [googleEvents, weekDates]);

  // Handle event selection
  const handleEventSelect = (event) => {
    if (selectedEventId === event.id) {
      setSelectedEventId(null);
      setSingleEvent(null);
    } else {
      setSelectedEventId(event.id);
      // setMessage(event.title);
      // console.log(googleEvents?.find(events => events?.id === event?.id));
      setSingleEvent(googleEvents?.find(events => events?.id === event?.id));
    }
  };

  const handleSend = async () => {
    if (!message.trim()) return;

    const userMessage = message.trim();
    
    // Add user message to chat
    const now = new Date();
    let hours = now.getHours();
    const minutes = now.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    const timestamp = `${hours}:${minutesStr} ${ampm}`;
    
    setChatMessages(prev => [...prev, { type: 'user', value: userMessage, timestamp }]);
    setMessage('');
    
    // Add thinking loader message
    const thinkingMessageId = Date.now();
    setChatMessages(prev => [...prev, { 
      type: 'eva', 
      value: 'Thinking...',
      id: thinkingMessageId,
      isThinking: true,
      timestamp // Use same timestamp as user message
    }]);
    
    try {
      setIsLoading(true);
      
      // Get auth token and user email from localStorage
      const authToken = localStorage.getItem('authToken');
      const userEmail = localStorage.getItem('userEmail');
      
      if (!authToken || !userEmail) {
        throw new Error('Authentication required');
      }

      // Prepare selected event data if an event is selected
      const selectedItems = [];
      if (singleEvent) {
        // Parse event times from Google Calendar event
        const startDateTime = singleEvent.start?.dateTime || singleEvent.start?.date;
        const endDateTime = singleEvent.end?.dateTime || singleEvent.end?.date;
        
        let eventTimeStart = '';
        let eventTimeEnd = '';
        let eventDate = '';
        
        if (startDateTime) {
          const startDate = new Date(startDateTime);
          eventTimeStart = startDate.toTimeString().split(' ')[0]; // HH:MM:SS format
          eventDate = startDate.toISOString().split('T')[0]; // YYYY-MM-DD format
        }
        
        if (endDateTime) {
          const endDate = new Date(endDateTime);
          eventTimeEnd = endDate.toTimeString().split(' ')[0]; // HH:MM:SS format
        }

        // Extract attendees' emails
        const attendeesEmails = [];
        if (singleEvent.attendees && Array.isArray(singleEvent.attendees)) {
          singleEvent.attendees.forEach(attendee => {
            if (attendee.email && !attendee.resource) {
              attendeesEmails.push(attendee.email);
            }
          });
        }

        selectedItems.push({
          event_uuid: singleEvent.id,
          event_name: singleEvent.summary || 'No Title',
          event_description: singleEvent.description || '',
          event_time_start: eventTimeStart,
          event_time_end: eventTimeEnd,
          event_date: eventDate,
          attendees: attendeesEmails
        });
      }


      // Session expiration time: 24 hours (in milliseconds)
      const SESSION_EXPIRY_TIME = 24 * 60 * 60 * 1000;
      
      // Generate or retrieve session ID with auto-refresh
      let sessionId = localStorage.getItem('evaSessionId');
      const sessionTimestamp = localStorage.getItem('evaSessionTimestamp');
      
      // Check if session exists and if it's expired or about to expire (within 1 hour)
      const now = Date.now();
      const shouldRefreshSession = !sessionId || 
                                   !sessionTimestamp || 
                                   (now - parseInt(sessionTimestamp)) >= (SESSION_EXPIRY_TIME - 60 * 60 * 1000);
      
      if (shouldRefreshSession) {
        // Generate new session ID
        sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('evaSessionId', sessionId);
        localStorage.setItem('evaSessionTimestamp', now.toString());
        console.log('Session refreshed:', sessionId);
      }


      // Make API call
      const backendUrl = 'https://eva-inbuilt-dossier-test-backend-1057220697534.us-central1.run.app';
      const response = await fetch(`${backendUrl}/agent_run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          selected_items: selectedItems,
          user_email_id: userEmail,
          session_id: sessionId,
          user_message: userMessage
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      
      // Replace thinking message with actual response
      updateMessage(thinkingMessageId, {
        type: 'eva',
        value: data.response || data.output || 'Request processed successfully',
        isThinking: false,
      });
      
      // Refresh calendar events to show any changes made by the API
      await fetchEvents();
      
      // Clear selected event after sending
      setSelectedEventId(null);
      setSingleEvent(null);
      
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.message || 'Failed to send message. Please try again.');
      
      // Replace thinking message with error message
      updateMessage(thinkingMessageId, {
        type: 'eva',
        value: 'Sorry, I encountered an error processing your request. Please try again.',
        isThinking: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrevWeek = () => {
    setCurrentWeekStart(prev => addWeeks(prev, -1));
  };

  const handleNextWeek = () => {
    setCurrentWeekStart(prev => addWeeks(prev, 1));
  };

  const handleToday = () => {
    setCurrentWeekStart(startOfWeek(startOfToday(), { weekStartsOn: 1 }));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#020617' }}> {/* slate-950 */}
      {/* Top Bar */}
      <Box
        sx={{
          maxWidth: '100%',
          height: 64, // h-16
          bgcolor: 'rgba(15, 23, 42, 0.5)', // slate-900/50
          backdropFilter: 'blur(12px)', // backdrop-blur-md
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          px: { xs: 2, sm: 3 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          zIndex: 20,
        }}
      >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 9 }}> {/* gap-12 from reference */}
            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  background: 'linear-gradient(to top right, #8B5CF6 0%, #06B6D4 100%)', // violet-500 to cyan-500
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  boxShadow: '0 10px 15px -3px rgba(139, 92, 246, 0.2)',
                }}
              >
                E
              </Box>
              <Box sx={{ 
                fontWeight: 'bold', 
                fontSize: '1.25rem', 
                color: 'white', 
                letterSpacing: '-0.05em',
                display: { xs: 'none', sm: 'block' },
              }}>
                EVA
              </Box>
            </Box>

            {/* Navigation Items */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
              {['CALENDAR', 'REPORTS', 'TASKS', 'SETTINGS'].map((item) => (
                <Box
                  key={item}
                  onClick={() => {
                    if (item === 'REPORTS') {
                      navigate('/reports');
                    }
                  }}
                  sx={{
                    px: 2,
                    py: 1,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    color: item === 'CALENDAR' ? 'white' : '#94A3B8', // slate-400
                    bgcolor: item === 'CALENDAR' ? 'rgba(255, 255, 255, 0.1)' : 'transparent', // white/10
                    borderRadius: '0.5rem',
                    boxShadow: item === 'CALENDAR' ? 'inset 0 2px 4px 0 rgba(255, 255, 255, 0.05)' : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    letterSpacing: '0.15em',
                    '&:hover': {
                      color: 'white',
                      bgcolor: item === 'CALENDAR' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  {item}
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Divider Line */}
            <Box sx={{ width: '1px', height: '24px', bgcolor: 'rgba(255, 255, 255, 0.1)', mx: 1 }} />
            
            {isSignedIn && userProfile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, pl: 2 }}>
                <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                  <Box sx={{ 
                    color: 'white', 
                    fontWeight: 500, 
                    fontSize: '0.875rem',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                  }}>
                    {userProfile.name || userProfile.email}
                  </Box>
                  <Box 
                    onClick={handleSignOut}
                    sx={{ 
                      color: '#94A3B8', // slate-400
                      fontSize: '0.625rem', 
                      cursor: 'pointer', 
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      '&:hover': { color: 'white' } 
                    }}
                  >
                    Sign Out
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: 'linear-gradient(to bottom right, #6366F1 0%, #06B6D4 100%)', // indigo-500 to cyan-500
                    border: '2px solid #1E293B', // slate-800
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    cursor: 'pointer',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </Box>
              </Box>
            ) : (
            <Button
              variant="contained"
              size="small"
              onClick={handleSignIn}
              disabled={isLoading}
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                textTransform: 'none',
                bgcolor: '#5B7FFF',
                color: 'white',
                '&:hover': {
                  bgcolor: '#7B5FFF',
                },
              }}
            >
              {isLoading ? 'Loading...' : 'Sign in with Google'}
            </Button>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {/* Chat Panel */}
        <ChatPanel
          chatMessages={chatMessages}
          message={message}
          setMessage={setMessage}
          handleSend={handleSend}
          isLoading={isLoading}
          chatPanelWidth={chatPanelWidth}
          handleMouseDown={handleMouseDown}
          isResizing={isResizing}
        />

        {/* Calendar View */}
        <Box
          sx={{
            flex: 1,
            minWidth: { md: '400px' }, // Minimum width for calendar visibility
            display: 'flex',
            overflow: 'hidden',
            width: '100%', // Ensure it takes available space
            maxWidth: '100%', // Prevent overflow
          }}
        >
          <CalendarView
            isSignedIn={isSignedIn}
            isLoading={isLoading}
            error={error}
            setError={setError}
            handleSignIn={handleSignIn}
            weekRangeText={weekRangeText}
            handlePrevWeek={handlePrevWeek}
            handleToday={handleToday}
            handleNextWeek={handleNextWeek}
            formattedDays={formattedDays}
            weekDates={weekDates}
            calendarEvents={calendarEvents}
            selectedEventId={selectedEventId}
            handleEventSelect={handleEventSelect}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default EvaCalendar;
